import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Input } from "@loot-os/ui/components/input";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { centsToDollars, dashIfEmpty } from "../lib/format";
import { parseTotalCostInput, replacesTracking } from "../lib/orderEdits";
import { useTRPC } from "../lib/trpc";

/** `orders.updateMetadata` with the page's toast and refetch. */
function useUpdateMetadata(saved: string) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  return useMutation(
    trpc.platform.fulfillment.orders.updateMetadata.mutationOptions({
      onSuccess: () => {
        toast.success(saved);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );
}

/**
 * TCG Fulfill Order's click-to-edit tracking number: saves when the field loses focus or on
 * Enter, as Retool did, but asks first before replacing or clearing a number the order already
 * has. Any status; the label flow writes it too. The tenant is told (`metadata_changed`), and a
 * shopper's withdrawal that shipped without one tells the shopper.
 */
export function OrderTrackingField({
  orderId,
  trackingNumber,
}: {
  orderId: string;
  trackingNumber: string | null;
}) {
  const save = useUpdateMetadata("Tracking number saved.");
  const [replacing, setReplacing] = useState<string | null>(null);
  const commit = (text: string) => save.mutate({ id: orderId, trackingNumber: text });

  return (
    <>
      <ClickToEdit
        label="Tracking"
        display={dashIfEmpty(trackingNumber)}
        initial={trackingNumber ?? ""}
        pending={save.isPending}
        className="font-mono text-xs"
        onCommit={(text) => {
          if (text.trim() === (trackingNumber ?? "").trim()) return true;
          if (replacesTracking(trackingNumber, text)) setReplacing(text);
          else commit(text);
          return true;
        }}
      />
      <AlertDialog open={replacing !== null} onOpenChange={(open) => !open && setReplacing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {replacing?.trim() ? "Replace the tracking number?" : "Clear the tracking number?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {replacing?.trim()
                ? `${trackingNumber} becomes ${replacing.trim()}.`
                : `${trackingNumber} is removed from the order.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep {trackingNumber}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (replacing !== null) commit(replacing);
                setReplacing(null);
              }}
            >
              {replacing?.trim() ? "Replace" : "Clear"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * TCG Fulfill Order's Total Order Cost: staff-entered fulfillment cost in dollars, read only by
 * the order-analysis report. Saves on blur or Enter; a blank clears it and 0 is $0.00 (Retool
 * sent 0 as no value).
 *
 * Deliberately unlike Retool, Loot OS computes this total from the order's lines and recomputes
 * it whenever a line is cancelled or added (`computeOrderTotals`), replacing a cost typed here.
 * boxed kept the two apart; one column, computed from the lines, is the chosen route (2026-10-16).
 */
export function OrderTotalCostField({
  orderId,
  totalCost,
}: {
  orderId: string;
  totalCost: number | null;
}) {
  const save = useUpdateMetadata("Total cost saved.");
  return (
    <ClickToEdit
      label="Total Order Cost"
      display={totalCost === null ? "—" : centsToDollars(totalCost)}
      initial={totalCost === null ? "" : (totalCost / 100).toFixed(2)}
      pending={save.isPending}
      inputMode="decimal"
      onCommit={(text) => {
        const parsed = parseTotalCostInput(text);
        if ("error" in parsed) {
          toast.error(parsed.error);
          return false;
        }
        if (parsed.cents !== totalCost) save.mutate({ id: orderId, totalCost: parsed.cents });
        return true;
      }}
    />
  );
}

/**
 * "Label: value", turned into a field by a click. Enter or leaving the field commits, Escape
 * cancels; `onCommit` answers whether the text was accepted. A refused text keeps the field open
 * on Enter, to correct it, and is dropped when the field is left: staying open there put up one
 * more error toast at every click elsewhere, with only Escape to get out.
 */
function ClickToEdit({
  label,
  display,
  initial,
  pending,
  className,
  inputMode,
  onCommit,
}: {
  label: string;
  display: string;
  initial: string;
  pending: boolean;
  className?: string;
  inputMode?: "decimal";
  onCommit: (text: string) => boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // Enter closes the field, and a browser may then report the field's blur: commit once.
  const committed = useRef(false);
  const open = () => {
    committed.current = false;
    setDraft(initial);
  };
  const finish = (leaving: boolean) => {
    if (draft === null || committed.current) return;
    if (onCommit(draft) || leaving) {
      committed.current = true;
      setDraft(null);
    }
  };

  if (draft === null) {
    return (
      <p className={className}>
        {label}:{" "}
        <button
          type="button"
          className="cursor-text underline decoration-dotted underline-offset-4 disabled:opacity-50"
          disabled={pending}
          title={`Edit ${label.toLowerCase()}`}
          onClick={open}
        >
          {display}
        </button>
      </p>
    );
  }
  return (
    <div className={`flex items-center gap-2 ${className ?? ""}`}>
      <span>{label}:</span>
      <Input
        aria-label={label}
        autoFocus
        className="h-7 max-w-56"
        value={draft}
        inputMode={inputMode}
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => finish(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            finish(false);
          }
          if (e.key === "Escape") {
            committed.current = true;
            setDraft(null);
          }
        }}
      />
    </div>
  );
}
